import React, { useEffect, useState } from 'react';

import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router';

import Button from '../../components/Button';
import Chip from '../../components/Chip';
import ContentCard from '../../components/ContentCard';
import ContentWrapper from '../../components/ContentWrapper';
import DataGrid from '../../components/DataGrid';
import PageContent from '../../components/PageContent';
import SkeletonStack from '../../components/SkeletonStack';
import { getCurrentUserId } from '../../services/auth';
import {
  useGetLessonQuery, useGetLessonsQuery, useRescheduleLessonMutation,
} from '../../services/classes';
import { Lesson } from '../../services/classes/types';
import { useGetCoursesQuery } from '../../services/courses';
import {
  getLessonClass, getLessonLabel,
} from '../../utils/common/classes';
import { DESIRED_LESSON_SIZE } from '../../utils/common/constants';
import useError from '../../utils/react/hooks/useError';
import useSnackbar from '../../utils/react/hooks/useSnackbar';
import useSuccess from '../../utils/react/hooks/useSuccess';

const DATA_GRID_COLUMNS = [
  {
    field: 'date',
    headerName: 'Date',
    width: 100,
  },
  {
    field: 'day',
    headerName: 'Day',
    width: 60,
  },
  {
    field: 'time',
    headerName: 'Time',
    width: 100,
  },
  {
    field: 'teacher',
    headerName: 'Teacher',
    width: 120,
  },
  {
    field: 'warning',
    headerName: '',
    width: 180,
    renderCell: (params: any) => (
      params.value && <Chip color="lightRed" label={params.value} size="small" />
    ),
  },
];

const StudentReschedulePage = () => {
  const { subject,
    classId,
    lesson: encodedLesson } = useParams();

  const navigate = useNavigate();
  const createSnackbar = useSnackbar();
  const studentId = getCurrentUserId();

  const lesson = decodeURIComponent(encodedLesson ?? '');

  const { data: courses, isLoading: isCoursesLoading } = useGetCoursesQuery({});

  const course = lesson?.split('#')[0];
  const lessonId = lesson?.split('#')[1];

  const { data: makeUpLessons, isLoading: isMakeUpLessonsLoading } = useGetLessonsQuery({
    course,
    lessonId,
    rollMarked: false,
  });

  const { data: fetchedLesson, isLoading: isLessonLoading } = useGetLessonQuery({
    subject: subject ?? '',
    classId: classId ?? '',
    lesson,
  });

  const [rescheduleLesson, { isLoading: isRescheduleLoading,
    isError: isRescheduleError,
    error: rescheduleError,
    isSuccess: rescheduleSuccess }] = useRescheduleLessonMutation();

  const [makeUpLessonRows, setMakeUpLessonRows] = useState<any[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | undefined>();

  const handleSelect = (l?: Lesson) => {
    if (l) {
      if (l.enrolledStudents.length >= DESIRED_LESSON_SIZE) {
        createSnackbar('Lesson is full', 'error');
        return;
      }

      setSelectedLesson(l);
    }
  };

  useEffect(() => {
    if (makeUpLessons) {
      // Set make up lesson rows but filter out the original lesson
      setMakeUpLessonRows(makeUpLessons.data.filter((l) => l.id !== fetchedLesson?.data.id)
        .sort((a, b) => a.startDatetime - b.startDatetime)
        // Filter lessons before current time or and more than 4 weeks after today
        .filter((l) => dayjs(l.startDatetime).isAfter(dayjs()))
        .map((l) => {
          const weekOfMakeUp = dayjs(l.startDatetime).startOf('week');
          const weekOfOriginal = dayjs(fetchedLesson?.data.startDatetime).startOf('week');

          const weeksDifference = weekOfMakeUp.diff(weekOfOriginal, 'week');
          let warning = '';

          if (weeksDifference < 0) {
            warning = `${-weeksDifference} week${-weeksDifference > 1 ? 's' : ''} before lesson`;
          } else if (weeksDifference > 0) {
            warning = `${weeksDifference} week${weeksDifference > 1 ? 's' : ''} after lesson`;
          } else {
            warning = '';
          }

          // If lesson is full, show warning
          if (l.enrolledStudents.length >= DESIRED_LESSON_SIZE) {
            warning = 'FULL';
          }

          return {
            id: l.id,
            date: dayjs(l.startDatetime).format('D MMM \'YY'),
            day: dayjs(l.startDatetime).format('ddd'),
            time: dayjs(l.startDatetime).format('h:mm a'),
            teacher: fetchedLesson?.metadata?.teacher?.typeOptions?.find((t) => t.value === l.teacher)?.label ?? 'Unassigned',
            studentCount: l.enrolledStudents.length,
            warning,
          };
        }));
    }
  }, [makeUpLessons]);

  const handleMakeUp = () => {
    if (subject && classId && selectedLesson && studentId) {
      rescheduleLesson({
        subject,
        oldClassId: classId,
        newClassId: getLessonClass(selectedLesson),
        oldLessonId: lesson,
        newLessonId: selectedLesson.lesson,
        studentId,
      });
    }
  };

  useEffect(() => {
    if (rescheduleSuccess) {
      navigate(-1);
    }
  }, [rescheduleSuccess]);

  useError(isRescheduleError, rescheduleError);
  useSuccess(rescheduleSuccess, 'Successfully rescheduled');

  if (
    isMakeUpLessonsLoading ||
    isLessonLoading ||
    isCoursesLoading
  ) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <PageContent>
      <Stack direction="column" spacing={3}>
        <ContentWrapper
          backButtonHref={-1}
          title={`Reschedule ${(fetchedLesson && courses) &&
            getLessonLabel(fetchedLesson?.data, courses.data)} (${fetchedLesson &&
              dayjs(fetchedLesson.data.startDatetime).format('h:mm a D MMM \'YY')})`}
        >
          <ContentCard title="Available Lessons" variant="outlined">
            <Alert severity="info">
              If none of the below times work for you,
              please reach out to @Elfie on Slack for more options.
            </Alert>
            <DataGrid
              rows={makeUpLessonRows}
              columns={DATA_GRID_COLUMNS}
              getRowId={(e) => e.id}
              loading={isMakeUpLessonsLoading}
              onRowClick={(e) =>
                handleSelect(makeUpLessons?.data?.find((l) => l.id === e.row.id) ?? undefined)}
              hideFooter
            />
          </ContentCard>
        </ContentWrapper>
      </Stack>
      <Dialog
        open={!!selectedLesson}
        onClose={() => setSelectedLesson(undefined)}
      >
        <DialogTitle>
          Reschedule to {selectedLesson?.startDatetime ? dayjs(selectedLesson.startDatetime).format('ddd h:mm a D MMM \'YY') : ''}
        </DialogTitle>
        <DialogContent>
          Make sure you can attend this new lesson time.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLesson(undefined)} variant="text" color="error">
            Cancel
          </Button>
          <Button onClick={handleMakeUp} loading={isRescheduleLoading} variant="contained">
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>
    </PageContent>
  );
};

export default StudentReschedulePage;
